import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';

import { dashboardT } from '../../../../lib/messages';
import { dashboardPercentChange, DashboardTrend, dashboardTrendTone } from '../../trend';

export interface DashboardMetricComparisonProps {
  current: number | undefined;
  previous: number | undefined;
  period?: string | null | undefined;
}

export function DashboardMetricComparison({
  current,
  previous,
  period,
}: DashboardMetricComparisonProps) {
  const change = dashboardPercentChange(current, previous);
  if (change === null) return null;
  const tone = dashboardTrendTone(change);

  let Icon = Minus;
  if (change > 0) Icon = ArrowUpRight;
  if (change < 0) Icon = ArrowDownRight;
  const comparisonLabel = period
    ? dashboardT('dashboard.comparison.period', { period })
    : dashboardT('dashboard.comparison.previous');
  // Longer periods always sit on their own line, so cards in a row line up; a short one sits
  // beside the change and only wraps as a whole on very narrow cards.
  const besideChange = period ? singleMonthPeriod.test(period) : false;

  return (
    <div
      className="mt-1 flex max-w-full flex-wrap items-baseline gap-x-1.5 gap-y-0.5 overflow-hidden text-xs font-medium tabular-nums"
      data-trend={tone.trend}
    >
      <span
        className={`inline-flex shrink-0 items-center gap-0.5 self-center ${comparisonToneClassName[tone.trend]}`}
        title={comparisonLabel}
      >
        <Icon aria-hidden="true" className="size-3" />
        {change === 0 ? '0.0%' : `${change > 0 ? '+' : ''}${change.toFixed(1)}%`}
      </span>
      <span
        className={`text-muted-foreground min-w-0 text-xs leading-tight font-normal ${besideChange ? 'whitespace-nowrap' : 'basis-full break-words whitespace-normal'}`}
        title={comparisonLabel}
      >
        {comparisonLabel}
      </span>
    </div>
  );
}

/** Day or days of one month without year or hours ("Oct 27", "Oct 15–21") fit beside the change. */
const singleMonthPeriod = /^\p{L}+ \d{1,2}(?:–\d{1,2})?$/u;

const comparisonToneClassName = {
  [DashboardTrend.Positive]: 'text-[var(--status-success-text)]',
  [DashboardTrend.Negative]: 'text-destructive',
  [DashboardTrend.Neutral]: 'text-muted-foreground',
} as const;
